import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { useTheme } from '../ThemeContext';

const AMBER_LIGHT = '#FEF3C7';

export default function SearchModal({ companyId, onClose, onSelectJob, onGoToCustomers, onGoToQuotes, onGoToMaterials }) {
  const { t } = useTheme();
  const s = getS(t);
  const [query, setQuery] = useState('');
  const [smartMode, setSmartMode] = useState(false);
  const [results, setResults] = useState({ jobs: [], customers: [], quotes: [], materials: [] });
  const [smartResults, setSmartResults] = useState([]);
  const [smartFilter, setSmartFilter] = useState(null); // { label, ...filterFields }
  const [loading, setLoading] = useState(false);
  const [smartLoading, setSmartLoading] = useState(false);
  const [smartError, setSmartError] = useState('');
  const inputRef = useRef(null);
  const overlayRef = useRef(null);
  const debounceRef = useRef(null);
  const smartDebounceRef = useRef(null);

  // Auto-focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // ── Quick search ──────────────────────────────────────────────────────────
  const search = useCallback(async (q) => {
    if (!q.trim() || !companyId) {
      setResults({ jobs: [], customers: [], quotes: [], materials: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    const like = `%${q}%`;
    const [jobsRes, customersRes, quotesRes, matsRes] = await Promise.all([
      supabase
        .from('jobs')
        .select('id, job_number, name, customers(name)')
        .eq('company_id', companyId)
        .or(`name.ilike.${like},job_number.ilike.${like},po_number.ilike.${like}`)
        .limit(5),
      supabase
        .from('customers')
        .select('id, name, email')
        .eq('company_id', companyId)
        .or(`name.ilike.${like},email.ilike.${like}`)
        .limit(5),
      supabase
        .from('quotes')
        .select('id, quote_number, name, customers(name)')
        .eq('company_id', companyId)
        .or(`quote_number.ilike.${like},name.ilike.${like}`)
        .limit(5),
      supabase
        .from('materials')
        .select('id, name, heat_number, material_type')
        .eq('company_id', companyId)
        .or(`name.ilike.${like},heat_number.ilike.${like}`)
        .limit(5),
    ]);
    setResults({
      jobs: jobsRes.data ?? [],
      customers: customersRes.data ?? [],
      quotes: quotesRes.data ?? [],
      materials: matsRes.data ?? [],
    });
    setLoading(false);
  }, [companyId]);

  // ── Smart search: call nl-search edge function, then execute the filter ──
  const smartSearch = useCallback(async (q) => {
    if (!q.trim() || !companyId) {
      setSmartResults([]);
      setSmartFilter(null);
      setSmartLoading(false);
      return;
    }
    setSmartLoading(true);
    setSmartError('');
    setSmartFilter(null);

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const token = currentSession?.access_token;

      const { data, error } = await supabase.functions.invoke('nl-search', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: { company_id: companyId, query: q },
      });

      if (error?.context?.status === 429) {
        setSmartError('AI limit reached. Try Quick Search or upgrade your plan.');
        setSmartLoading(false);
        return;
      }
      if (error || !data?.filter) {
        setSmartError('Smart Search failed. Try Quick Search.');
        setSmartLoading(false);
        return;
      }

      const filter = data.filter;
      const label = data.label || q;
      setSmartFilter({ ...filter, label });

      // Execute the filter against jobs
      let dbQuery = supabase
        .from('jobs')
        .select('id, job_number, name, estimated_value, due_date, current_stage_id, customers(name), stage_templates:current_stage_id(name)')
        .eq('company_id', companyId)
        .or('archived.is.null,archived.eq.false')
        .order('created_at', { ascending: false })
        .limit(20);

      if (filter.min_value) dbQuery = dbQuery.gte('estimated_value', filter.min_value);
      if (filter.max_value) dbQuery = dbQuery.lte('estimated_value', filter.max_value);
      if (filter.overdue) {
        const todayStr = new Date().toISOString().slice(0, 10);
        dbQuery = dbQuery.lt('due_date', todayStr).neq('status', 'completed');
      }
      if (filter.due_within_days) {
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + filter.due_within_days);
        dbQuery = dbQuery.lte('due_date', futureDate.toISOString().slice(0, 10));
      }
      if (filter.search_text) {
        const like = `%${filter.search_text}%`;
        dbQuery = dbQuery.or(`name.ilike.${like},job_number.ilike.${like},po_number.ilike.${like}`);
      }

      const { data: jobs } = await dbQuery;
      let filtered = jobs ?? [];

      // Client-side filters that can't easily be done in Supabase query
      if (filter.stage) {
        const stageLower = String(filter.stage).toLowerCase();
        filtered = filtered.filter((j) =>
          (j.stage_templates?.name ?? '').toLowerCase() === stageLower
        );
      }
      if (filter.customer) {
        const custLower = String(filter.customer).toLowerCase();
        filtered = filtered.filter((j) =>
          (j.customers?.name ?? '').toLowerCase().includes(custLower)
        );
      }

      setSmartResults(filtered);
    } catch (err) {
      console.error('[SmartSearch] error:', err);
      setSmartError('Smart Search encountered an error.');
    } finally {
      setSmartLoading(false);
    }
  }, [companyId]);

  // Debounced quick search
  useEffect(() => {
    if (smartMode) return;
    clearTimeout(debounceRef.current);
    if (query.trim()) setLoading(true);
    debounceRef.current = setTimeout(() => search(query), 280);
    return () => clearTimeout(debounceRef.current);
  }, [query, search, smartMode]);

  // Debounced smart search
  useEffect(() => {
    if (!smartMode) return;
    clearTimeout(smartDebounceRef.current);
    if (query.trim()) setSmartLoading(true);
    smartDebounceRef.current = setTimeout(() => smartSearch(query), 600);
    return () => clearTimeout(smartDebounceRef.current);
  }, [query, smartSearch, smartMode]);

  // Clear results when switching modes
  useEffect(() => {
    setResults({ jobs: [], customers: [], quotes: [], materials: [] });
    setSmartResults([]);
    setSmartFilter(null);
    setSmartError('');
  }, [smartMode]);

  const total = results.jobs.length + results.customers.length + results.quotes.length + results.materials.length;
  const hasQuery = query.trim().length > 0;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const isLoading = smartMode ? smartLoading : loading;

  return (
    <div ref={overlayRef} style={s.overlay} onClick={handleOverlayClick}>
      <div style={s.modal} role="dialog" aria-modal="true" aria-label="Global search">
        {/* Search input row */}
        <div style={s.inputRow}>
          <span style={s.searchIcon}>⌕</span>
          <input
            ref={inputRef}
            style={s.input}
            type="text"
            placeholder={smartMode
              ? 'e.g. "steel jobs over $5k behind schedule"…'
              : 'Search jobs, customers, quotes, materials…'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
          {isLoading && <span style={s.spinner}>↻</span>}
          <kbd style={s.escKey} onClick={onClose}>Esc</kbd>
        </div>

        {/* Mode toggle row */}
        <div style={s.modeRow}>
          <button
            style={{ ...s.modeBtn, ...(smartMode ? {} : s.modeBtnActive) }}
            onClick={() => setSmartMode(false)}
          >
            Quick Search
          </button>
          <button
            style={{ ...s.modeBtn, ...(smartMode ? s.modeBtnActive : {}) }}
            onClick={() => setSmartMode(true)}
          >
            ✦ Smart Search
          </button>

          {smartMode && (
            <span style={s.smartHint}>
              Ask in plain English — AI interprets your query
            </span>
          )}
        </div>

        {/* Smart filter tag */}
        {smartMode && hasQuery && smartFilter && !smartLoading && (
          <div style={s.filterTag}>
            <span style={s.filterTagIcon}>✦</span>
            <span style={s.filterTagText}>Filtering by: {smartFilter.label}</span>
            <span style={s.filterTagCount}>{smartResults.length} job{smartResults.length !== 1 ? 's' : ''}</span>
          </div>
        )}

        {/* Divider */}
        <div style={s.divider} />

        {/* Results area */}
        <div style={s.resultsArea}>
          {/* Smart mode results */}
          {smartMode && (
            <>
              {!hasQuery && (
                <div style={s.emptyState}>
                  <span style={s.emptyIcon}>✦</span>
                  <p style={s.emptyText}>Try: "steel jobs over $5k", "Delta Machining jobs this month", "jobs behind schedule"</p>
                </div>
              )}

              {hasQuery && smartError && (
                <div style={s.emptyState}>
                  <p style={{ ...s.emptyText, color: '#DC2626' }}>{smartError}</p>
                </div>
              )}

              {hasQuery && !smartLoading && !smartError && smartResults.length === 0 && smartFilter && (
                <div style={s.emptyState}>
                  <p style={s.emptyText}>No jobs match "<strong>{query}</strong>"</p>
                </div>
              )}

              {smartResults.length > 0 && (
                <div style={s.group}>
                  <div style={s.groupLabel}>
                    <span style={s.groupIcon}>📋</span> Jobs
                  </div>
                  {smartResults.map((job) => {
                    const value = job.estimated_value
                      ? `$${parseFloat(job.estimated_value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                      : null;
                    const dueStr = job.due_date ? job.due_date.slice(0, 10) : null;
                    const stageName = job.stage_templates?.name ?? null;
                    return (
                      <button
                        key={job.id}
                        style={s.resultBtn}
                        onClick={() => { onSelectJob(job.id); onClose(); }}
                      >
                        <div style={s.resultLeft}>
                          <span style={s.resultTitle}>
                            {job.job_number && <span style={s.resultNum}>{job.job_number}</span>}
                            {job.job_number ? ' — ' : ''}{job.name}
                          </span>
                          <span style={s.resultSub}>
                            {job.customers?.name && <>{job.customers.name}</>}
                            {stageName && <> · {stageName}</>}
                            {value && <> · {value}</>}
                            {dueStr && <> · Due {dueStr}</>}
                          </span>
                        </div>
                        <span style={s.resultArrow}>→</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Quick mode results */}
          {!smartMode && (
            <>
              {!hasQuery && (
                <div style={s.emptyState}>
                  <span style={s.emptyIcon}>⌘</span>
                  <p style={s.emptyText}>Search across all your jobs, customers, quotes, and materials</p>
                </div>
              )}

              {hasQuery && !loading && total === 0 && (
                <div style={s.emptyState}>
                  <p style={s.emptyText}>No results found for "<strong>{query}</strong>"</p>
                </div>
              )}

              {/* Jobs */}
              {results.jobs.length > 0 && (
                <div style={s.group}>
                  <div style={s.groupLabel}>
                    <span style={s.groupIcon}>📋</span> Jobs
                  </div>
                  {results.jobs.map((job) => (
                    <button
                      key={job.id}
                      style={s.resultBtn}
                      onClick={() => { onSelectJob(job.id); onClose(); }}
                    >
                      <div style={s.resultLeft}>
                        <span style={s.resultTitle}>
                          {job.job_number && <span style={s.resultNum}>{job.job_number}</span>}
                          {job.job_number ? ' — ' : ''}{job.name}
                        </span>
                        {job.customers?.name && <span style={s.resultSub}>{job.customers.name}</span>}
                      </div>
                      <span style={s.resultArrow}>→</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Customers */}
              {results.customers.length > 0 && (
                <div style={s.group}>
                  <div style={s.groupLabel}>
                    <span style={s.groupIcon}>🏢</span> Customers
                  </div>
                  {results.customers.map((c) => (
                    <button
                      key={c.id}
                      style={s.resultBtn}
                      onClick={() => { onGoToCustomers(); onClose(); }}
                    >
                      <div style={s.resultLeft}>
                        <span style={s.resultTitle}>{c.name}</span>
                        {c.email && <span style={s.resultSub}>{c.email}</span>}
                      </div>
                      <span style={s.resultArrow}>→</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Quotes */}
              {results.quotes.length > 0 && (
                <div style={s.group}>
                  <div style={s.groupLabel}>
                    <span style={s.groupIcon}>💬</span> Quotes
                  </div>
                  {results.quotes.map((q) => (
                    <button
                      key={q.id}
                      style={s.resultBtn}
                      onClick={() => { onGoToQuotes(); onClose(); }}
                    >
                      <div style={s.resultLeft}>
                        <span style={s.resultTitle}>
                          {q.quote_number && <span style={s.resultNum}>{q.quote_number}</span>}
                          {q.quote_number && q.name ? ' — ' : ''}{q.name}
                        </span>
                        {q.customers?.name && <span style={s.resultSub}>{q.customers.name}</span>}
                      </div>
                      <span style={s.resultArrow}>→</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Materials */}
              {results.materials.length > 0 && (
                <div style={s.group}>
                  <div style={s.groupLabel}>
                    <span style={s.groupIcon}>🔩</span> Materials
                  </div>
                  {results.materials.map((m) => (
                    <button
                      key={m.id}
                      style={s.resultBtn}
                      onClick={() => { onGoToMaterials(); onClose(); }}
                    >
                      <div style={s.resultLeft}>
                        <span style={s.resultTitle}>{m.name}</span>
                        <span style={s.resultSub}>
                          {m.material_type}
                          {m.heat_number ? ` · Heat: ${m.heat_number}` : ''}
                        </span>
                      </div>
                      <span style={s.resultArrow}>→</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const getS = (t) => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: t.overlay,
    zIndex: 2000,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '12vh',
    backdropFilter: 'blur(2px)',
  },
  modal: {
    width: '100%',
    maxWidth: 620,
    backgroundColor: t.card,
    borderRadius: 14,
    boxShadow: t.shadowLg,
    overflow: 'hidden',
    fontFamily: "'Inter', system-ui, sans-serif",
    maxHeight: '76vh',
    display: 'flex',
    flexDirection: 'column',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px',
    gap: 10,
    flexShrink: 0,
  },
  searchIcon: {
    fontSize: 22,
    color: t.text4,
    flexShrink: 0,
    lineHeight: 1,
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: 17,
    color: t.text1,
    padding: '18px 0',
    backgroundColor: 'transparent',
    fontFamily: 'inherit',
  },
  spinner: {
    fontSize: 18,
    color: '#D97706',
    animation: 'spin 1s linear infinite',
    flexShrink: 0,
  },
  escKey: {
    border: `1px solid ${t.border1}`,
    borderRadius: 5,
    padding: '3px 7px',
    fontSize: 11,
    color: t.text4,
    fontFamily: 'inherit',
    cursor: 'pointer',
    backgroundColor: t.card2,
    flexShrink: 0,
  },
  modeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '0 16px 10px',
    flexShrink: 0,
  },
  modeBtn: {
    padding: '4px 12px',
    borderRadius: 20,
    border: `1px solid ${t.border1}`,
    backgroundColor: 'transparent',
    color: t.text4,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background-color 0.15s, color 0.15s, border-color 0.15s',
  },
  modeBtnActive: {
    backgroundColor: AMBER_LIGHT,
    color: '#92400E',
    borderColor: '#FDE68A',
    fontWeight: 700,
  },
  smartHint: {
    marginLeft: 6,
    fontSize: 11,
    color: t.text4,
    fontStyle: 'italic',
  },
  filterTag: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 16px 8px',
    backgroundColor: AMBER_LIGHT,
    flexShrink: 0,
  },
  filterTagIcon: {
    fontSize: 11,
    color: '#92400E',
  },
  filterTagText: {
    fontSize: 12,
    color: '#92400E',
    fontWeight: 500,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  filterTagCount: {
    fontSize: 11,
    color: '#B45309',
    fontWeight: 700,
    flexShrink: 0,
  },
  divider: {
    height: 1,
    backgroundColor: t.border2,
    flexShrink: 0,
  },
  resultsArea: {
    overflowY: 'auto',
    flex: 1,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 24px',
    gap: 10,
  },
  emptyIcon: {
    fontSize: 32,
    color: t.border1,
  },
  emptyText: {
    margin: 0,
    fontSize: 14,
    color: t.text4,
    textAlign: 'center',
    lineHeight: 1.5,
  },
  group: {
    padding: '8px 0 4px',
  },
  groupLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: t.text4,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    padding: '4px 16px 6px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  groupIcon: {
    fontSize: 13,
  },
  resultBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    border: 'none',
    borderRadius: 0,
    background: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    transition: 'background-color 0.1s ease',
  },
  resultLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    flex: 1,
  },
  resultTitle: {
    fontSize: 14,
    color: t.text1,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  resultNum: {
    color: '#D97706',
    fontWeight: 700,
  },
  resultSub: {
    fontSize: 12,
    color: t.text4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  resultArrow: {
    fontSize: 14,
    color: t.borderInput,
    flexShrink: 0,
    marginLeft: 8,
  },
});
